export default class Logger {
    #restPath;
    #requester;

    constructor(restPath = '', requester = null) {
        this.#restPath = restPath.endsWith('/') ? restPath : restPath + '/';
        this.#requester = requester || Logger.defaultRequester;
    }

    static async defaultRequester(url, params) {
        const formData = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            formData.append(key, value);
        }

        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
    }

    /**
     * Sends a log request to the standard backend log controller endpoint (log/log).
     *
     * Since this is an async method returning a Promise, the caller can 'await' it
     * or use '.then()' / '.catch()' to react when the logging request completes.
     *
     * @param {string} code - The error/log code (e.g. 'E1790').
     * @param {string} message - The description or log message.
     * @param {string} severity - Log level severity ('info', 'warn', 'error').
     * @param {Object|string} data - Optional metadata object/array (automatically JSON-serialized) or raw string.
     * @returns {Promise<any>}
     */
    async log(code, message = '', severity = 'info', data = {}) {
        const payload = {
            code,
            message,
            severity,
            data: typeof data === 'string' ? data : JSON.stringify(data)
        };
        const url = this.#restPath + 'log/log';

        return this.#requester(url, payload);
    }

    /**
     * Sends a log request to the custom backend log controller endpoint (log/customlog).
     *
     * Since this is an async method returning a Promise, the caller can 'await' it
     * or use '.then()' / '.catch()' to react when the logging request completes.
     *
     * @param {string} channel - The log channel/file destination name.
     * @param {string} message - The description or log message.
     * @param {Object|string} data - Optional metadata object/array (automatically JSON-serialized) or raw string.
     * @returns {Promise<any>}
     */
    async customLog(channel, message = '', data = {}) {
        const payload = {
            channel,
            message,
            data: typeof data === 'string' ? data : JSON.stringify(data)
        };
        const url = this.#restPath + 'log/customlog';

        return this.#requester(url, payload);
    }
}
